import React from 'react';
import { PropInfo, CodeExample, InlineEditProps } from '@cimpress/react-components';

import InlineEditCode from '!raw-loader!./inlineedit.jsx';
import InlineEditDemo from './inlineedit.jsx';
import ComponentDoc from '../../shared/TabbedComponentDoc';

const InlineEditDocs = () => {
  const props: PropInfo<InlineEditProps>[] = [
    {
      name: 'name',
      type: 'string',
      default: '',
      description: 'Name attribute for the input field',
    },
    {
      name: 'value',
      type: 'string or number',
      default: '',
      description: 'Input value of the input field',
    },
    {
      name: 'label',
      type: 'string',
      default: '',
      description: (
        <div>
          Label for an input field. Providing a label is optional, unless the field is <code>required</code>.
        </div>
      ),
    },
    {
      name: 'placeholder',
      type: 'string',
      default: '',
      description: <div>Placeholder text for an input field and resting text when missing a value.</div>,
    },
    {
      name: 'id',
      type: 'string',
      default: '',
      description: 'ID prop for the input field',
    },
    {
      name: 'required',
      type: 'boolean',
      default: 'false',
      description: (
        <div>
          Marks the inline edit as required. Only applicable if a <code>label</code> is provided
        </div>
      ),
    },
    {
      name: 'disabled',
      type: 'boolean',
      default: 'false',
      description: 'Will disable the ability to interact with the inline edit',
    },
    {
      name: 'requiredWarningMessage',
      type: 'string',
      default: 'This Field is Required',
      description: 'This prop can be used to localize the warning to display when a field is required',
    },
    {
      name: 'size',
      type: 'sting',
      default: 'default',
      description: (
        <span>
          Determines the size of the input. Can be one of <code>small</code>, <code>default</code>, <code>h1</code>,
          <code>h2</code>, <code>h3</code>, <code>h4</code>, <code>h5</code>, or <code>h6</code>.
        </span>
      ),
    },
    {
      name: 'type',
      type: 'string',
      default: 'text',
      description: (
        <span>
          Determines the{' '}
          <a
            href="https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input"
            target="_blank"
            rel="noopener noreferrer"
          >
            HTML5 input type
          </a>{' '}
          of the input. One of <code>text</code>, <code>number</code>, <code>password</code>, <code>url</code>,{' '}
          <code>search</code>, <code>email</code>, or <code>tel</code>. You can also provide <code>textarea</code> to
          get a textarea instead of an input element.
        </span>
      ),
    },
    {
      name: 'onChange',
      type: 'function',
      default: '',
      description: (
        <div>
          <p>Callback function that is fired when the inline edit's value changes.</p>
          <p>
            Signature: <code>function(event: object) {'=>'} void</code>{' '}
          </p>
          <p>
            <code>event:</code> Change event targeting the inline edit.
          </p>
        </div>
      ),
    },
    {
      name: 'onSave',
      type: 'function',
      default: '',
      description: (
        <div>
          <p>Callback function that is fired when a user clicks the save button or presses enter. </p>
          <p>
            If using onSave it can be optionally paired with <code>onCancel</code>. Do not use with{' '}
            <code>onChange</code>
          </p>
          <p>
            Signature: <code>function(inputTarget: object, event: object) {'=>'} void</code>{' '}
          </p>
          <p>
            <code>inputTarget:</code> object containing <code>value: string</code> and <code>name: string</code>.
          </p>
          <p>
            <code>event:</code> keyboard event or mouse event triggered when user saves changes.
          </p>
        </div>
      ),
    },
    {
      name: 'onCancel',
      type: 'function',
      default: '',
      description: (
        <div>
          <p>Callback function that is fired when the cancel button is clicked. </p>
          <p>
            This callback is optional but can be used in conjunction with <code>onSuccess</code>.
          </p>
          <p>
            Signature: <code>function(value: string, event: object) {'=>'} void</code>{' '}
          </p>
          <p>
            <code>value:</code> The input fields value(useful if changes were made and you'd like to save a draft).
          </p>
          <p>
            <code>event:</code> Click event targeting the cancel button.
          </p>
        </div>
      ),
    },
    {
      name: 'onFocus',
      type: 'function',
      default: '',
      description: 'Callback function that is fired when the inline edit is focused.',
    },
    {
      name: 'onBlur',
      type: 'function',
      default: '',
      description: 'Callback function that is fired when the inline edit loses focus.',
    },
    {
      name: 'validateInput',
      type: 'function',
      default: '',
      description: (
        <div>
          <p>Function that is invoked to validate the text input during edited. </p>
          <p>This callback is optional.</p>
          <p>It will not be invoked if the onChange handler is provided.</p>
          <p>
            Signature: <code>function(text: string) {'=>'} string | null | undefined</code>{' '}
          </p>
          <p>
            <code>text:</code> The input to be validated.
          </p>
          <p>
            <code>returns:</code> The error message or null/undefined if valid.
          </p>
        </div>
      ),
    },
    {
      name: 'minWidth',
      type: 'number',
      default: '130',
      description: 'Specifies the minimum width of the control',
    },
    {
      name: 'onEditStateChange',
      type: 'function',
      default: '',
      description: (
        <div>
          <p>Optional callback function that is fired when the editing starts and ends.</p>
          <p>
            Signature: <code>function(isEditing: boolean) {'=>'} void</code>{' '}
          </p>
        </div>
      ),
    },
  ];
  return (
    <ComponentDoc name="Inline Edit" propInfos={props}>
      <InlineEditDemo />
      <CodeExample code={InlineEditCode} />
    </ComponentDoc>
  );
};

export default InlineEditDocs;
